import { useTranslation } from 'next-i18next'
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js'
import { useCallback, useContext, useState } from 'react'

import { useAnalyticsEvent } from '../../hooks/analyticsEvent'
import { EventHookInterface } from '../../types'
import { StripeContext, UserContext } from '../../context'
import { StripeCardPurchaseFormComponent } from './StripeCardPurchaseFormComponent'

export const StripeCardPurchaseForm: React.FC<{
  onClose: (success?: boolean) => void
  eventHook: EventHookInterface
}> = ({ eventHook, onClose }) => {
  const { t } = useTranslation('common')
  const { currentUser } = useContext(UserContext)

  const [loading, setLoading] = useState(false)

  const { value, addCurrentUserToEvent } = eventHook

  const stripe = useStripe()
  const elements = useElements()
  const analyticEvent = useAnalyticsEvent()

  const [error, setError] = useState('')

  const handleChange = useCallback(() => {
    if (error) {
      setError('')
    }
  }, [error])

  const { createPaymentIntent } = useContext(StripeContext)

  const purchase = useCallback(async () => {
    setError('')

    if (!stripe || !elements) {
      // Stripe.js has not yet loaded.
      // Make sure to disable form submission until Stripe.js has loaded.
      return
    }

    if (!currentUser.full_name) {
      setError(
        t(
          'Please update your name in settings page',
          'Please update your name in settings page',
        ),
      )
      return
    }

    setLoading(true)
    analyticEvent.fbAddPaymentInfo()
    let purchaseSuccess = false

    try {
      const intentResult = await createPaymentIntent({
        event_id: value.id,
        group_id: value.hosted_group.id,
      }).catch((error) => {
        console.error(error)
        setError(
          t(
            'Error occurs while processing your request',
            'Error occurs while processing your request',
          ),
        )
      })
      // console.log(intentResult)
      if (intentResult) {
        const { client_secret, payment_intent_id } = intentResult

        const result = await stripe.confirmCardPayment(client_secret, {
          payment_method: {
            card: elements.getElement(CardElement),
            billing_details: {
              name: currentUser.full_name,
            },
          },
        })

        if (result.error) {
          setError(result.error.message)
        } else {
          // The payment has been processed!
          if (result.paymentIntent.status === 'succeeded') {
            await addCurrentUserToEvent(payment_intent_id)
              .then(() => {
                analyticEvent.fbPurchase(value.price.toFixed(2))
                purchaseSuccess = true
              })
              .catch((error) => {
                setError(
                  t(
                    'Error occurs while processing your purchase',
                    'Error occurs while processing your purchase',
                  ),
                )
              })
          }
        }
      }
    } catch (error) {
      console.log(error)
    } finally {
      setLoading(false)
      if (purchaseSuccess) {
        onClose(true)
      }
    }
  }, [
    addCurrentUserToEvent,
    analyticEvent,
    createPaymentIntent,
    currentUser.full_name,
    elements,
    onClose,
    setLoading,
    stripe,
    t,
    value.hosted_group.id,
    value.id,
    value.price,
  ])

  return (
    <StripeCardPurchaseFormComponent
      error={error}
      onClose={onClose}
      purchase={purchase}
      loading={loading}
      onCardDetailsChanged={handleChange}
      event={value}
    />
  )
}
